/**
 * Shared animation primitives for the DS core (production sections).
 *   - LazyMotion + `m` → ~4.6kb base instead of the full ~34kb `motion` bundle;
 *     `domAnimation` features only. `strict` forbids the heavy `motion.*` API.
 *   - transform/opacity-only reveals (no layout props) → no CLS.
 *   - reveal once, on scroll, and only what the visitor has not seen yet.
 *   - useReducedMotion → fully disabled when the OS asks for reduced motion.
 *
 * ⚠ The site is prerendered, and the prerender must never hide anything (DIT-553).
 * It used to: every reveal was rendered with its hidden starting state baked into
 * the HTML (`opacity:0;transform:translateY(26px)`), and only the bundle could
 * undo it. So the page was blank below the fold until hydration for everyone, and
 * blank for good for anyone whose bundle never ran — a stale asset after a deploy,
 * a blocked script, a runtime error — and for anyone with Reduce Motion on, whose
 * client took a branch that declined to animate and so never cleared it.
 *
 * Now the HTML is always the resting, visible state. The hidden state exists only
 * in the running app, and only for what is still below the fold when it starts:
 * that content is hidden while it is off screen (so nobody sees it disappear) and
 * revealed as it scrolls in, exactly as before. Whatever was already on screen at
 * hydration simply stays — there is nothing to reveal about content being read.
 */
import { LazyMotion, domAnimation, m, useInView, useReducedMotion, type Transition } from "motion/react";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type CSSProperties, type RefObject } from "react";

export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={domAnimation} strict>
      {children}
    </LazyMotion>
  );
}

/** House easing — matches the Framer project's tween feel. */
export const EASE = [0.22, 0.61, 0.36, 1] as const;

/** A motion target — the element's look before or after its reveal. */
type Target = { opacity?: number; x?: number; y?: number; scale?: number };

export type ScrollRevealOptions = {
  /** Where a reveal starts: the hidden look it animates FROM. */
  from: Target;
  /** The resting look. The prerender, and every path that does not animate, render this. */
  to: Target;
  /** How the reveal animates into `to`. */
  transition?: Transition;
  /**
   * How much of the element must be in view before it reveals. Zero by default:
   * the moment any of it is on screen. A threshold leaves a card the visitor can
   * already see sitting blank until enough of it has scrolled in — QA measured a
   * 407px card still empty with 60px of it showing, and read the empty area as
   * part of the flicker (DIT-553).
   */
  amount?: number;
  /** Never animate — for above-the-fold (LCP) content, and for UI that must not wait. */
  eager?: boolean;
};

/** An empty ref for `useInView` to watch when there is nothing to watch. */
const NO_ELEMENT: RefObject<Element | null> = { current: null };

// useLayoutEffect warns under a server render on older Reacts; the decision it
// makes is client-only anyway.
const useClientLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * The props that make a motion element reveal on scroll without the prerender
 * ever hiding it. Spread them onto an `m.*` (or `motion.*`) element.
 *
 * The decision to animate is taken ONCE, in a layout effect at hydration, from
 * where the element is on screen at that moment: below the fold → hide it and
 * reveal it when it scrolls in; anywhere else → leave it alone. A layout effect
 * so the hide lands before the browser paints the hydrated frame — though since
 * it only ever applies to content off screen, nobody could see it either way.
 *
 * `data-reveal` says which it became ("in-view" or "static"): the stylesheet's
 * reduced-motion rule keys on it, and so does the e2e that forbids a backdrop
 * filter inside something that animates.
 */
export function useScrollReveal<E extends Element>({ from, to, transition, amount = 0, eager = false }: ScrollRevealOptions) {
  const reduce = useReducedMotion();
  const ref = useRef<E>(null);
  const [armed, setArmed] = useState(false);

  useClientLayoutEffect(() => {
    if (reduce || eager) return;
    const el = ref.current;
    if (el && el.getBoundingClientRect().top >= window.innerHeight) setArmed(true);
    // Decided once, at hydration, on purpose: re-deciding on every scroll would
    // hide content the visitor has already read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch only what was armed. Content that was on screen at hydration, eager
  // content and reduced-motion content never hide, so they never need an
  // IntersectionObserver — and the funnel's plan tiles, which are eager, must
  // not start one on every render.
  const inView = useInView((armed ? ref : NO_ELEMENT) as RefObject<Element>, { once: true, amount });
  const hidden = armed && !inView;

  return {
    ref,
    initial: false as const,
    animate: hidden ? from : to,
    // Going INTO hidden is instant — it happens off screen, at hydration.
    transition: hidden ? { duration: 0 } : transition,
    "data-reveal": armed ? "in-view" : "static",
  };
}

/**
 * Has the app hydrated? Module-level on purpose: something that mounts LATER — a
 * slide swapped in, a page reached by a client-side link — must see `true` on its
 * very first render, and per-instance state would start at false every time.
 * Set once, by `markHydrated()` from the marketing layout's first effect; it can
 * never be true during the prerender, where effects do not run.
 */
let hydrated = false;

/** Call once the app has hydrated — from the marketing layout's first effect. */
export function markHydrated() {
  hydrated = true;
}

/**
 * `initial` for a MOUNT animation (`initial` + `animate`, no scroll trigger) that
 * must not hide anything in the prerender (DIT-553). Everything rendered in the
 * first pass — the prerender and the hydration that must match it — starts in its
 * resting state, so the HTML shows it; anything that mounts afterwards gets its
 * entrance as before.
 *
 * Nothing visible is given up by this. Every mount animation on the site sits
 * below the first screen (measured on desktop and mobile, every page that has
 * one), so its entrance used to play off screen at hydration — the visitor never
 * saw it; they only saw what was left behind, which is now also what the HTML
 * shows before any script runs.
 */
export function entrance<T extends object>(from: T): T | false {
  return hydrated ? from : false;
}

type RevealTag = "div" | "section" | "span" | "li" | "h1" | "h2" | "h3" | "p";

type RevealProps = {
  children: ReactNode;
  /** Semantic tag to render (keeps real h1/p in the DOM for SEO). */
  as?: RevealTag;
  delay?: number;
  /** Initial vertical offset in px (transform only → compositor, no layout). */
  y?: number;
  /**
   * Above-the-fold: never animate. Use for LCP content (e.g. the hero heading) —
   * the reveal would only delay it.
   */
  eager?: boolean;
  className?: string;
  style?: CSSProperties;
};

/** Reveal-on-scroll wrapper. Animates only opacity + transform. */
export function Reveal({ children, as = "div", delay = 0, y = 26, eager = false, className, style }: RevealProps) {
  const Comp = m[as] as typeof m.div;
  const reveal = useScrollReveal<HTMLDivElement>({
    from: { opacity: 0, y },
    to: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: EASE, delay },
    eager,
  });

  return (
    <Comp className={className} style={style} {...reveal}>
      {children}
    </Comp>
  );
}
